import React from 'react';
import styled from 'styled-components';

const Panel = styled.div`
  flex: ${props => props.$collapsed ? '0 0 auto' : '1'};
  border-bottom: 1px solid #e1e4e8;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: ${props => props.$collapsed ? 'visible' : 'hidden'};
`;

const Title = styled.h3`
  margin: 0;
  padding: 16px;
  font-size: 14px;
  font-weight: 600;
  color: #24292f;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #e1e4e8;
  background: #f6f8fa;
  flex-shrink: 0;
  cursor: move;
  user-select: none;
  
  &:hover {
    background: #eaeef2;
  }
`;

const CollapseIcon = styled.span`
  margin-left: auto;
  cursor: pointer;
  padding: 4px;
  transition: transform 0.2s;
  transform: ${props => props.$collapsed ? 'rotate(-90deg)' : 'rotate(0deg)'};
  
  &:hover {
    background: rgba(0,0,0,0.1);
    border-radius: 3px;
  }
`;

const Badge = styled.span`
  background: ${props => props.$count > 0 ? '#d1242f' : '#28a745'};
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
`;

const ErrorList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const ErrorItem = styled.div`
  padding: 10px 12px;
  margin: 6px 0;
  border-radius: 6px;
  font-size: 12px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: normal;
  max-width: 100%;
  overflow: hidden;

  background: ${props => 
    props.$type === 'error' ? '#ffeef0' : 
    props.$type === 'warning' ? '#fff3cd' : '#e7f3ff'};
  border-left: 3px solid ${props => 
    props.$type === 'error' ? '#d1242f' : 
    props.$type === 'warning' ? '#f59e0b' : '#0969da'};
  border-right: 1px solid ${props => 
    props.$type === 'error' ? '#f85149' : 
    props.$type === 'warning' ? '#ffc107' : '#0969da'};
  border-top: 1px solid ${props => 
    props.$type === 'error' ? '#f85149' : 
    props.$type === 'warning' ? '#ffc107' : '#0969da'};
  border-bottom: 1px solid ${props => 
    props.$type === 'error' ? '#f85149' : 
    props.$type === 'warning' ? '#ffc107' : '#0969da'};
  color: ${props => 
    props.$type === 'error' ? '#d1242f' : 
    props.$type === 'warning' ? '#856404' : '#0969da'};
  transition: all 0.15s;
  
  &:hover {
    opacity: ${props => props.$fixable ? '0.9' : '1'};
    transform: ${props => props.$fixable ? 'translateX(-2px)' : 'none'};
    box-shadow: ${props => props.$fixable ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'};
  }
`;

const LineNumber = styled.span`
  font-weight: bold;
  margin-right: 8px;
  opacity: 0.7;
`;

const FixButton = styled.span`
  float: right;
  font-size: 10px;
  padding: 2px 6px;
  background: rgba(255,255,255,0.3);
  border-radius: 3px;
  font-weight: 600;
  transition: all 0.15s;
  
  &:hover {
    background: rgba(255,255,255,0.5);
  }
`;

const NoErrors = styled.div`
  color: #28a745;
  font-size: 13px;
  padding: 16px;
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  border: 2px solid #28a745;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.2);
`;

function ValidationPanel({ errors, onFixError, format, onErrorClick, collapsed, onToggle, draggable, onDragStart, onDragOver, onDragEnd }) {
  const handleErrorClick = (error) => {
    if (error.line && onErrorClick && format === 'yaml') {
      onErrorClick(error.line);
    }
    if (error.fix && onFixError) {
      onFixError(error);
    }
  };

  return (
    <Panel $collapsed={collapsed}>
      <Title 
        draggable={draggable}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
      >
        ✅ Validation
        <Badge $count={errors.length}>{errors.length}</Badge>
        <CollapseIcon $collapsed={collapsed} onClick={(e) => { e.stopPropagation(); onToggle && onToggle(); }}>
          ▼
        </CollapseIcon>
      </Title>
      {!collapsed && <ErrorList>
        {errors.length === 0 ? (
          <NoErrors>✓ No validation errors</NoErrors>
        ) : (
          errors.map((error, index) => (
            <ErrorItem 
              key={index} 
              $type={error.type || 'error'}
              $fixable={!!error.fix || (!!error.line && format === 'yaml')}
              onClick={() => handleErrorClick(error)}
              style={{ cursor: (error.fix || (error.line && format === 'yaml')) ? 'pointer' : 'default' }}
            >
              {error.line && format === 'yaml' && <LineNumber>Line {error.line}:</LineNumber>}
              {error.message}
              {error.fix && <FixButton>🔧 FIX</FixButton>}
            </ErrorItem>
          ))
        )}
      </ErrorList>}
    </Panel>
  );
}

export default ValidationPanel;